<script setup lang="ts">
// Rail lateral (formato desktop) — porte de `.rail`/`.rail-brand`/`.rail-i`/`.rail-b`/`.rail-foot`
// (Docs/V2/Clinica/clinica/styles.css + app.jsx): 236px, fundo navy, borda esquerda teal no ativo.
//
// Além do protótipo: item é `<RouterLink>` (não `<button>`), com `aria-current`, e o contador tem
// rótulo textual acessível.
import { RouterLink } from 'vue-router';
import type { BadgeKey, Destination } from '@/router/destinations';
import { useTheme } from '@/composables/theme';

const props = defineProps<{
  items: readonly Destination[];
  badges: Readonly<Partial<Record<BadgeKey, number>>>;
  activeName: string | null;
  /** Rodapé da rail — no protótipo, o nome da clínica logada. */
  footer: string | null;
}>();

const { isDark, toggleTheme } = useTheme();

function badgeOf(item: Destination): number {
  return item.badgeKey === undefined ? 0 : (props.badges[item.badgeKey] ?? 0);
}
</script>

<template>
  <nav
    class="rail"
    aria-label="Navegação principal"
  >
    <div class="rail-brand">
      <div class="bn">
        Dentis<span>Tech</span>
      </div>
      <div class="bs">
        Clínicas
      </div>
    </div>
    <div class="rail-items">
      <RouterLink
        v-for="item in items"
        :key="item.name"
        class="rail-i"
        :class="{ on: item.name === activeName }"
        :to="item.path"
        :aria-current="item.name === activeName ? 'page' : undefined"
      >
        <span
          class="ri-ico"
          aria-hidden="true"
        >{{ item.icon }}</span>
        <span class="ri-l">{{ item.title }}</span>
        <span
          v-if="badgeOf(item) > 0"
          class="rail-b"
        >
          <span aria-hidden="true">{{ badgeOf(item) }}</span>
          <span class="rail-sr">{{ badgeOf(item) }} pendência(s) em {{ item.title }}</span>
        </span>
      </RouterLink>
    </div>
    <div class="rail-foot">
      <span class="rail-foot-text">{{ footer ?? 'Clínica' }}</span>
      <button
        class="rail-theme-btn"
        type="button"
        :aria-label="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
        :title="isDark ? 'Mudar para tema claro' : 'Mudar para tema escuro'"
        @click="toggleTheme"
      >
        {{ isDark ? '☀️' : '🌙' }}
      </button>
    </div>
  </nav>
</template>

<style scoped>
/*
 * Transcrição literal de `.rail*`. As escalas de branco sobre o navy são as do protótipo
 * (.08 / .05 / .58 / .92 / .4). Os tokens `--dt-rail-*` do Design System carregam as escalas do
 * Operacional (.55 / .3) — próximas, mas NÃO iguais. Como este app tem fidelidade literal como
 * critério de aceite, as escalas ficam declaradas aqui, nomeadas, e não espalhadas pelas regras.
 */
.rail {
  --app-rail-line: rgba(255, 255, 255, 0.08);
  --app-rail-hover: rgba(255, 255, 255, 0.05);
  --app-rail-text: rgba(255, 255, 255, 0.58);
  --app-rail-text-hover: rgba(255, 255, 255, 0.92);
  --app-rail-muted: rgba(255, 255, 255, 0.4);
  --app-rail-active: rgba(50, 200, 175, 0.12);

  width: 236px;
  background: var(--dt-navy);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}
.rail-brand { padding: 18px; border-bottom: 1px solid var(--app-rail-line); }
.rail-brand .bn { font-size: 16px; font-weight: var(--dt-font-weight-extrabold); color: #fff; letter-spacing: -0.02em; }
.rail-brand .bn span { color: var(--dt-teal); }
.rail-brand .bs {
  font-size: 9px;
  color: var(--app-rail-muted);
  margin-top: 4px;
  font-weight: var(--dt-font-weight-semibold);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.rail-items { flex: 1; padding: 12px 0; }
.rail-i {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: none;
  border: none;
  border-left: 2px solid transparent;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: var(--dt-font-weight-medium);
  color: var(--app-rail-text);
  cursor: pointer;
  text-align: left;
  min-height: 44px;
  text-decoration: none;
  position: relative;
  transition: background 0.15s ease, color 0.15s ease;
}
.rail-i:hover { background: var(--app-rail-hover); color: var(--app-rail-text-hover); }
.rail-i.on { background: var(--app-rail-active); color: #fff; border-left-color: var(--dt-teal); }
/* 24px e não 18px: um emoji em 15px ocupa ~21px de avanço e escapava da caixa (overflow
   horizontal medido no rail em todas as larguras de desktop). */
.rail-i .ri-ico { font-size: 15px; width: var(--dt-space-lg); text-align: center; flex-shrink: 0; }
.rail-i .ri-l { flex: 1; }
.rail-b {
  min-width: 19px;
  height: 18px;
  padding: 0 6px;
  border-radius: 20px;
  background: var(--dt-error);
  color: #fff;
  font-size: 10px;
  font-weight: var(--dt-font-weight-extrabold);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.rail-foot {
  padding: 12px 16px;
  border-top: 1px solid var(--app-rail-line);
  font-size: 11px;
  color: var(--app-rail-muted);
  font-weight: var(--dt-font-weight-semibold);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.rail-foot-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rail-theme-btn {
  background: var(--app-rail-hover);
  border: 1px solid var(--app-rail-line);
  color: #fff;
  border-radius: 8px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  transition: background 0.15s ease, transform 0.15s ease;
}
.rail-theme-btn:hover {
  background: rgba(255, 255, 255, 0.12);
}
.rail-theme-btn:active {
  transform: scale(0.92);
}
.rail-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
</style>
